import { useCallback, useEffect, useState } from 'react';

import { SearchMode } from '../utils/searchMatch';
import {
  SEARCH_MODE_EVENT,
  getStoredSearchMode,
  setStoredSearchMode,
} from '../utils/searchModeStorage';

/**
 * Single source of truth for the metadata search mode (general vs exact).
 *
 * Owns the persisted preference and keeps every consumer in sync: the native
 * `storage` event covers other tabs, and a window event (`SEARCH_MODE_EVENT`)
 * covers other instances in the same tab (e.g. several metadata questions on one
 * page, plus the SearchBar checkbox). Used by both the SearchBar checkbox and the
 * question components that filter on the mode.
 */
export function useSearchMode() {
  const [searchMode, setSearchModeState] = useState<SearchMode>(getStoredSearchMode);

  useEffect(() => {
    const sync = () => setSearchModeState(getStoredSearchMode());
    window.addEventListener('storage', sync);
    window.addEventListener(SEARCH_MODE_EVENT, sync);
    return () => {
      window.removeEventListener('storage', sync);
      window.removeEventListener(SEARCH_MODE_EVENT, sync);
    };
  }, []);

  const setSearchMode = useCallback((mode: SearchMode) => {
    setStoredSearchMode(mode);
    setSearchModeState(mode);
    window.dispatchEvent(new Event(SEARCH_MODE_EVENT));
  }, []);

  const setExactMatch = useCallback(
    (checked: boolean) => setSearchMode(checked ? 'exact' : 'general'),
    [setSearchMode],
  );

  return { searchMode, isExact: searchMode === 'exact', setExactMatch };
}
